import moment from 'moment';
import _, { isArray, compact, map, forEach } from 'lodash'; // Required for test coverage purpose
import { getAudienceSize, isAudienceSizeTooSmall } from '../react/services/AudienceInsightsService';
import { PERMISSION } from '../infra/utils/export';
import { FIRST_PARTY_SOURCE_ENUM } from '../pages/first_party/first_party_constants';
import { CHANNEL_IDS } from './audience-channels';

const md5 = require('js-md5');
const COUNTRIES = {
  US: { id: '840', label: 'United States', cc: 'US' },
  GB: { label: 'United Kingdom', cc: 'GB' },
  AU: { label: 'Australia', cc: 'AU' },
  CA: { label: 'Canada', cc: 'CA' },
  DE: { label: 'Germany', cc: 'DE' },
  FR: { label: 'France', cc: 'FR' },
};

// France (Hisense ACR) exposes linear ads only: no amplification data (deterministic-only
// activation) and Linear-only commercials. PHOB-1813.
// Case-insensitive: callers pass the country code in different cases — CommercialsModal gets
// the lowercased `default_geo.cc` from the AngularJS template, while AudienceTargetModal gets
// the uppercase `cc` from getGeoFromAudienceSegment.
const isFranceGeo = (geo) => geo?.toLowerCase() === COUNTRIES.FR?.cc.toLowerCase();
/*
* Properties:
*   name- channel name (string)
*   isGeoMandatory- some channels are limited to countries because limited audience profiles (boolean)
*   limitedGeos- limited countries company have audience profiles (list of geos)
*   tablePrefix: table prefix saved in DB (string)
*   isTv - is tv channel (boolean)
*   advancedTvTypes- advanced tv types (object)
*   isSgTelco- is SG Telco (boolean)
* 
*  e.g.
*   hisense: {
        name: 'hisense',
        isGeoMandatory: true,
        limitedGeos: [COUNTRIES.US?.cc, COUNTRIES.GB?.cc, COUNTRIES.AU?.cc],
        tablePrefix: 'hisense',
        isTv: false,
        advancedTvTypes: {
            both: "tv-advanced",
            linear: "tv-advanced", 
            streaming: "tv-advanced",
        },
    },
* */

const demographicsSegmentByType = (type) => ({
  value: type ? `${type}Demographics` : 'demographics',
  label: 'Demographics',
  image: './images/pages/audience-builder/images/demographics_img.png',
});
const commercialsSegmentByType = (type) => ({
  value: type ? `${type}Commercials` : 'commercials',
  label: 'Commercials',
  image: './images/pages/audience-builder/images/commercials_img.png',
});

// [SEGMENT_ROW_TYPES.SMART_TV_DEMOGRAPHICS.value, SEGMENT_ROW_TYPES.INTERESTS.value, SEGMENT_ROW_TYPES.WEBSITES.value, SEGMENT_ROW_TYPES.LIFESTYLE.value, SEGMENT_ROW_TYPES.FIRST_PARTY.value].includes(segmentTypeValue);
const SEGMENT_ROW_TYPES = {
  DEMOGRAPHICS: demographicsSegmentByType(),
  SMART_TV_DEMOGRAPHICS: demographicsSegmentByType('smartTv'),
  LINKEDIN_DEMOGRAPHICS: demographicsSegmentByType('linkedin'),
  INTERESTS: {
    value: 'interests',
    label: 'Interests',
    image: './images/pages/audience-builder/images/interests_img.png',
  },
  WEBSITES: { value: 'websites', label: 'Websites', image: './images/pages/audience-builder/images/websites_img.png' },
  LIFESTYLE: {
    value: 'lifestyle',
    label: 'Lifestyles',
    image: './images/pages/audience-builder/images/lifestyle_img.png',
  },
  FIRST_PARTY: {
    value: '1st party',
    label: '1st Party',
    image: './images/pages/audience-builder/images/1st_party_img.jpg',
    permission: 'first party segments',
  },
  TV_SHOWS: {
    value: 'tvShows',
    label: 'TV Shows',
    image: './images/pages/audience-builder/images/tv_shows_img.jpg',
    permission: 'tv',
  },
  ADVANCED_TV_SHOWS: {
    value: 'advancedTvShows',
    label: 'TV Shows',
    image: './images/pages/audience-builder/images/tv_shows_img.jpg',
    permission: 'tv',
  },
  APPS: { value: 'apps', label: 'Apps', image: './images/pages/audience-builder/images/apps_img.png' },
  COMMERCIALS: commercialsSegmentByType(),
  SMART_TV_COMMERCIALS: commercialsSegmentByType('smartTv'),
  LINKEDIN_INDUSTRIES: {
    value: 'linkedinIndustries',
    label: 'Industries',
    image: './images/pages/audience-builder/images/industries_img.jpg',
  },
  LINKEDIN_COMPANIES: {
    value: 'linkedinCompanies',
    label: 'Companies',
    image: './images/pages/audience-builder/images/companies_img.jpg',
  },
  LINKEDIN_JOBS: { value: 'linkedinJobs', label: 'Jobs', image: './images/pages/audience-builder/images/jobs_img.jpg' },
};
const CHANNELS = {
  hisense: {
    name: 'hisense',
    isGeoMandatory: true,
    limitedGeos: {
      [COUNTRIES.US?.cc]: { disabledSegments: [] }, // {US:{disabledSegments:[] , somethingElse: {}/[]/''}}
      [COUNTRIES.GB?.cc]: { disabledSegments: [] },
      [COUNTRIES.AU?.cc]: {
        disabledSegments: [
          SEGMENT_ROW_TYPES.SMART_TV_DEMOGRAPHICS.value,
          SEGMENT_ROW_TYPES.INTERESTS.value,
          SEGMENT_ROW_TYPES.WEBSITES.value,
          SEGMENT_ROW_TYPES.LIFESTYLE.value,
          SEGMENT_ROW_TYPES.FIRST_PARTY.value,
        ],
      },
      [COUNTRIES.CA?.cc]: {
        disabledSegments: [
          SEGMENT_ROW_TYPES.SMART_TV_DEMOGRAPHICS.value,
          SEGMENT_ROW_TYPES.INTERESTS.value,
          SEGMENT_ROW_TYPES.WEBSITES.value,
          SEGMENT_ROW_TYPES.LIFESTYLE.value,
          SEGMENT_ROW_TYPES.FIRST_PARTY.value,
        ],
      },
      [COUNTRIES.DE?.cc]: {
        disabledSegments: [
          SEGMENT_ROW_TYPES.SMART_TV_DEMOGRAPHICS.value,
          SEGMENT_ROW_TYPES.INTERESTS.value,
          SEGMENT_ROW_TYPES.WEBSITES.value,
          SEGMENT_ROW_TYPES.LIFESTYLE.value,
          SEGMENT_ROW_TYPES.FIRST_PARTY.value,
        ],
      },
      // France: linear ads only — additionally disable TV Shows, leaving Commercials only. PHOB-1813.
      [COUNTRIES.FR?.cc]: {
        disabledSegments: [
          SEGMENT_ROW_TYPES.SMART_TV_DEMOGRAPHICS.value,
          SEGMENT_ROW_TYPES.INTERESTS.value,
          SEGMENT_ROW_TYPES.WEBSITES.value,
          SEGMENT_ROW_TYPES.LIFESTYLE.value,
          SEGMENT_ROW_TYPES.FIRST_PARTY.value,
          SEGMENT_ROW_TYPES.ADVANCED_TV_SHOWS.value,
        ],
      },
    },
    tablePrefix: 'hisense',
    advancedTvTypes: {
      both: 'tv-advanced',
      linear: 'tv-advanced',
      streaming: 'tv-advanced',
    },
  },
  smart_tv_inscape: {
    name: 'smart_tv_inscape',
    tablePrefix: 'inscape',
    isTv: true,
  },
  tivo: {
    name: 'tivo',
    tablePrefix: 'tivo_ccp',
    isTv: true,
    advancedTvTypes: {
      both: 'tv-advanced',
      linear: 'tv-advanced',
      streaming: 'tv-amplify',
    },
  },
  peerlogix: {
    name: 'peerlogix',
    tablePrefix: 'peerlogix',
    advancedTvTypes: {
      streaming: 'tv-advanced',
    },
  },
  unified_tv: {
    name: 'unified_tv',
    isGeoMandatory: true,
    tablePrefix: 'unified_tv',
    advancedTvTypes: {
      both: 'tv-advanced',
      linear: 'tv-advanced',
      streaming: 'tv-advanced',
    },
  },
  linkedin: {
    name: 'linkedin',
  },
  data_spark: {
    name: 'data_spark',
    isSgTelco: true,
    tablePrefix: 'data_spark',
  },
  sg_bidstream: {
    name: 'sg_bidstream',
    isSgTelco: true,
  },
  snbb: {
    name: 'snbb',
    isSgTelco: true,
  },
  au_telco: {
    name: 'au_telco',
  },
  articles: {
    name: 'articles',
  },
  bidstream: {
    name: 'bidstream',
  },
};
const KEY_NAME_MAP = {
  geo: 'countries',
  age: 'ages',
  gender: 'genders',
  children: 'children',
  income: 'new-incomes',
  ethnicity: 'new-races',
};
const DEMOGRAPHICS_FIELDS = ['age', 'geo', 'income', 'ethnicity', 'gender', 'children', 'states'];
const TV_SHOWS_FIELDS = ['networks', 'genres', 'tv'];
const NEW_QUERY_TV_SHOWS_FIELDS = ['genres'];
const LEVEL_OF_INTENT_MAP = { searches: 'iw', 'frequent-keywords': 'xw', keywords: 'aw' };
const COMMERCIALS_MAPPING_MAP = {
  AD: 'ad-ids', //IDS
  PARENTS: 'parent-ids', // IDS
  BRANDS: 'brand-ids', // IDS
  CATEGORIES: 'category-ids', // IDS
  PRODUCTS: 'product-ids', // IDS
  EXCLUDED_BRANDS: 'excluded-brand-ids', // IDS
  EXCLUDED_PRODUCTS: 'excluded-product-ids', // IDS
  EXCLUDED_CREATIVES: 'excluded-ad-ids', // IDS
  EXPOSURE: 'exposure',
};
const COMMERCIALS_DESC_MAP = {
  creatives: COMMERCIALS_MAPPING_MAP.AD,
  brandParents: COMMERCIALS_MAPPING_MAP.PARENTS,
  brands: COMMERCIALS_MAPPING_MAP.BRANDS,
  categories: COMMERCIALS_MAPPING_MAP.CATEGORIES,
  products: COMMERCIALS_MAPPING_MAP.PRODUCTS,
  excludedBrands: COMMERCIALS_MAPPING_MAP.EXCLUDED_BRANDS,
  excludedProducts: COMMERCIALS_MAPPING_MAP.EXCLUDED_PRODUCTS,
  excludedCreatives: COMMERCIALS_MAPPING_MAP.EXCLUDED_CREATIVES,
  channel: 'table-name',
  exposure: COMMERCIALS_MAPPING_MAP.EXPOSURE,
};
const COMMERCIALS_QUERY_KEY_FIELDS = [
  'creatives',
  'brands',
  'brandParents',
  'categories',
  'products',
  'excludedBrands',
  'excludedProducts',
  'excludedCreatives',
];
const COMMERCIALS_ID_LIST_FIELDS = [
  COMMERCIALS_MAPPING_MAP.AD,
  COMMERCIALS_MAPPING_MAP.PARENTS,
  COMMERCIALS_MAPPING_MAP.BRANDS,
  COMMERCIALS_MAPPING_MAP.CATEGORIES,
  COMMERCIALS_MAPPING_MAP.PRODUCTS,
  COMMERCIALS_MAPPING_MAP.EXCLUDED_BRANDS,
  COMMERCIALS_MAPPING_MAP.EXCLUDED_PRODUCTS,
  COMMERCIALS_MAPPING_MAP.EXCLUDED_CREATIVES,
  COMMERCIALS_MAPPING_MAP.EXPOSURE,
];
const COMMON_MAPPING_MAP = {
  START_TIME: 'start-time',
  END_TIME: 'end-time',
};
const TV_SHOWS_MAPPING_MAP = {
  NETWORK: 'pnws', // IDS
  PROGRAM_SERIES: 'program-series-ids', // IDS
  SHOW: 'show-ids', // IDS
  GENRE: 'genres', //IDS
  EXPOSURE: 'exposure',
};
const TV_SHOWS_FILTER_MAPPING_MAP = { networks: TV_SHOWS_MAPPING_MAP.NETWORK, exposure: TV_SHOWS_MAPPING_MAP.EXPOSURE };
const ADVANCED_TV_SHOWS_MAPPING_MAP = {
  NETWORK: 'pnws', // IDS
  PROGRAM_SERIES: 'program-series-ids', // IDS
  SHOW: 'show-ids', // IDS
  GENRE: 'genre-ids', //IDS
  QUALIFIED_CAPPING: 'qualified-capping',
  EXPOSURE: 'exposure',
  NUMBER_OF_OBJECTS: 'number-of-objects',
  VIEWERSHIP_MODES: 'viewership-mode',
  TYPE: 'type',
};
const ADVANCED_TV_SHOWS_FILTER_MAPPING_MAP = {
  genres: ADVANCED_TV_SHOWS_MAPPING_MAP.GENRE,
  tv: ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES,
  episodes: ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW,
  networks: ADVANCED_TV_SHOWS_MAPPING_MAP.NETWORK,
  exposure: ADVANCED_TV_SHOWS_MAPPING_MAP.EXPOSURE,
  viewershipModes: ADVANCED_TV_SHOWS_MAPPING_MAP.VIEWERSHIP_MODES,
  viewEventQual: ADVANCED_TV_SHOWS_MAPPING_MAP.QUALIFIED_CAPPING,
};
// The fields of an advanced-TV filter-mapping that actually define a TV audience. Metadata that is
// always present (viewing-type, table-name, type, timeframe, qualified-capping) must NOT count toward
// "has a selection" — otherwise a source with no shows still looks non-empty and we emit a broken TV
// query (empty ids against a *_media table → backend 500). PHOB-1857: unified TV re-channels per source,
// so the leg for a source with none of its own shows must drop the TV constraint and query only the
// shared (non-TV) parts of the segment.
const ADVANCED_TV_SELECTION_KEYS = [
  ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES,
  ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW,
  ADVANCED_TV_SHOWS_MAPPING_MAP.NETWORK,
  ADVANCED_TV_SHOWS_MAPPING_MAP.GENRE,
];
function hasAdvancedTvSelections(filterMapping) {
  return !!filterMapping && ADVANCED_TV_SELECTION_KEYS.some((key) => !_.isEmpty(filterMapping[key]));
}
const AND_DELIMITER = ' <ii>and</ii> ',
  OR_DELIMITER = ' <ii>or</ii> ',
  NOT_DELIMITER = ' <ii>without</ii> ';
const SEGMENT_ROW_MAP = {
  lifestyle: { label: 'Lifestyles', icon: 'icon-lifestyle' },
  demographics: { label: 'Demographics', icon: 'icon-profile' },
  country: { label: 'Country', icon: 'icon-geo' },
  interests: { label: 'Interests', icon: 'icon-interests' },
  websites: { label: 'Websites', icon: 'icon-tv-monitor' },
  tvShows: { label: 'TV Shows', icon: 'icon-tv-shows' },
  advancedTvShows: { label: 'TV Shows', icon: 'icon-tv-shows' },
  '1st party': { label: '1st Party', icon: 'icon-1st-party' },
  linkedinDemographics: { label: 'Demographics', icon: 'icon-profile' },
  linkedinIndustries: { label: 'Industries', icon: 'icon-industries' },
  linkedinCompanies: { label: 'Companies', icon: 'icon-companies' },
  linkedinJobs: { label: 'Jobs', icon: 'icon-jobs' },
  smartTvDemographics: { label: 'Demographics', icon: 'icon-profile' },
  apps: { label: 'Apps', icon: 'icon-industries' },
  smartTvCommercials: { label: 'Commercials', icon: 'icon-commercials' },
};
const AND_LOGICAL_OPERAND = { value: 'and', label: 'ALL of the attributes below' };
const OR_LOGICAL_OPERAND = { value: 'or', label: 'ANY of the attributes below' };
const LOGICAL_OPERANDS = [AND_LOGICAL_OPERAND, OR_LOGICAL_OPERAND];

const SEGMENT_TYPES = {
  DEMOGRAPHICS: 'demographics',
  SMART_TV_DEMOGRAPHICS: 'smartTvDemographics',
  COUNTRY: 'country',
  LINKEDIN_DEMOGRAPHICS: 'linkedinDemographics',
  LINKEDIN_INDUSTRIES: 'linkedinIndustries',
  LINKEDIN_COMPANIES: 'linkedinCompanies',
  LINKEDIN_JOBS: 'linkedinJobs',
  APPS: 'apps',
  SMART_TV_COMMERCIALS: 'smartTvCommercials',
  TV_SHOWS: 'tvShows',
  ADVANCED_TV_SHOWS: 'advancedTvShows',
  COMMERCIALS: 'commercials',
  FIRST_PARTY: '1st party',
  LINKEDIN: 'linkedin',
  SMART_TV: 'smartTv',
  TV: 'tv',
  ADVANCED_TV: 'advancedTv',
};

const TV_SHOWS_SEGMENT_TYPES = [SEGMENT_TYPES.TV_SHOWS, SEGMENT_TYPES.ADVANCED_TV_SHOWS];
const COMMERCIALS_SEGMENT_TYPES = [SEGMENT_TYPES.SMART_TV_COMMERCIALS];
const DEMOGRAPHICS_SEGMENT_TYPES = [
  SEGMENT_TYPES.DEMOGRAPHICS,
  SEGMENT_TYPES.SMART_TV_DEMOGRAPHICS,
  SEGMENT_TYPES.COUNTRY,
];

const GENDER_VALUES = [
  { value: 'audience_s12', label: 'Male', icon: 'male', summary: 'male' },
  { value: 'audience_s11', label: 'Female', icon: 'female', summary: 'female' },
  { value: 'all', label: 'Both', icon: 'male_female', summary: 'all' },
];

const AGES = ['13-17', '18-20', '21-24', '25-34', '35-44', '45-49', '50-54', '55-64', '65+'];
const AUDIENCE_AGE_VALUES = map(AGES, (age) => ({ label: age, summary: age, value: age }));
const SPECIFIC_AGES = ['18-20', '21-24', '45-49', '50-54'];

//TODO - remove ethnicity from filters-partition and use this instead
const ETHNICITY_VALUES = [
  { label: 'African American', summary: 'African American', value: 'black' },
  { label: 'Asian American', summary: 'Asian American', value: 'asian' },
  { label: 'Caucasian', summary: 'Caucasian', value: 'white' },
  { label: 'Hispanic', summary: 'Hispanic', value: 'hispanic' },
];

const SG_ETHNICITY_VALUES = [
  { label: 'Chinese', summary: 'Chinese', value: 'chinese' },
  { label: 'Malay', summary: 'Malay', value: 'malay' },
  { label: 'Indian', summary: 'Indian', value: 'indian' },
  { label: 'Other', summary: 'Other ethnicities', value: 'other' },
];

//TODO - remove income from filters-partition and use this instead
const INCOME_VALUES = [
  { label: '0-25K', summary: '0-25k', value: ['0-15k', '15-25k'] },
  { label: '25-50K', summary: '25-50k', value: ['25-35k', '35-50k'] },
  { label: '50-75K', summary: '50-75k', value: '50-75k' },
  { label: '75-100K', summary: '75-100k', value: '75-100k' },
  { label: '100-150K', summary: '100-150k', value: '100-150k' },
  { label: '150-200K', summary: '150-200k', value: '150-200k' },
  { label: '200K+', summary: '200k+', value: '200k+' },
];

const DISPLAY_INCOME_VALUES = [
  { value: 0, label: '$0K' },
  { value: 25, label: '$25K' },
  { value: 50, label: '$50K' },
  { value: 75, label: '$75K' },
  { value: 100, label: '$100K' },
  { value: 150, label: '$150K' },
  { value: 200, label: '$200K' },
  { value: 250, label: 'Over $200K' },
];
const LEVELS_OF_EXPOSURE_MAP = {
  low: { value: 'low', label: 'Low' },
  medium: { value: 'medium', label: 'Medium' },
  high: { value: 'high', label: 'High' },
};
const LEVELS_OF_EXPOSURE = [LEVELS_OF_EXPOSURE_MAP.low, LEVELS_OF_EXPOSURE_MAP.medium, LEVELS_OF_EXPOSURE_MAP.high];

const VIEWERSHIP_MODES = [
  { label: 'Live', value: 'L' },
  { label: 'Viewed on same day', value: 'L+SD' },
  { label: 'Viewed 1-3 days later', value: 'L+3' },
  { label: 'Viewed 4-7 days later', value: 'L+7' },
  { label: 'Viewed 8 or more days later', value: ['L+30', 'L+X'] },
];

const VIEWING_EVENT_QUALIFIERS = [
  { label: 'Less than 1 minute', value: 0 },
  { label: '1 minute and up', value: 1 },
  { label: '3 minutes and up', value: 3 },
  { label: '5 minutes and up', value: 5 },
  { label: '7 minutes and up', value: 7 },
  { label: '10 minutes and up', value: 10 },
];
const VIEWING_TYPES_MAP = {
  both: { label: 'Both', value: 'both' },
  streaming: { label: 'Streaming', value: 'streaming' },
  linear: { label: 'Linear', value: 'linear' },
};
const VIEWING_TYPES = [VIEWING_TYPES_MAP.both, VIEWING_TYPES_MAP.streaming, VIEWING_TYPES_MAP.linear];

const UNIFIED_TV_SOURCES = [
  { label: 'ACR', value: 'hisense', colorClass: 'tv-source-hisense' },
  { label: 'Streaming', value: 'peerlogix', colorClass: 'tv-source-peerlogix' },
];

const SYNDICATION_PARENT_MEDIA_PROVIDERS = {
  TREMOR_RX_US: { name: 'Tremor RX - US', id: '785972280' },
  TREMOR_RX_EU: { name: 'Tremor RX - EU', id: '785972380' },
  TREMOR_RX_APAC: { name: 'Tremor RX - APAC', id: '785972381' },
};

const SYNDICATION_VALUES = _.map(SYNDICATION_PARENT_MEDIA_PROVIDERS, ({ name, id }) => ({ label: name, value: id }));

const isAllLevelsOfExposure = (levels) => isArray(levels) && levels.length >= LEVELS_OF_EXPOSURE.length;

const MOMENT_DATE_FORMAT = 'MM-DD-YY';
const dateToString = (date) => moment(new Date(date)).format(MOMENT_DATE_FORMAT);

const LEVEL_OF_EXPOSURE_COMMERCIALS_TOOLTIP =
  'Choose the exposure tertiaries of your audience to the selected brands and creatives';
const LEVEL_OF_EXPOSURE_TV_SHOWS_TOOLTIP = 'Choose the exposure tertiaries of your audience to the selected TV content';

const isAllValuesSelected = (selectedValues, values) =>
  !selectedValues.length || selectedValues.length === values.length;

const summaryTextBuilderByValues = (selectedValues) =>
  selectedValues.length === 1 ? selectedValues[0]['label'] : selectedValues.map((value) => value.label).join(', ');
const genericSummaryTextBuilder = (selectedValues, values, allValuesText) =>
  isAllValuesSelected(selectedValues, values) ? allValuesText : summaryTextBuilderByValues(selectedValues);
const getSummaryTextBuilder = (defaultText) => (selectedValues, values) =>
  genericSummaryTextBuilder(selectedValues, values, defaultText);

const deterministicPermissionName = PERMISSION.CREATE_DETERMINISTIC_SEGMENT;
const customPricePermissionName = PERMISSION.ALLOW_CUSTOM_PRICE_SEGMENT;
const optimizeSegmentPermissionName = PERMISSION.OPTIMIZE_SEGMENT;
const showAudienceIpCountPermissionName = PERMISSION.SHOW_AUDIENCE_IP_COUNT;

const DEFAULT_MANDATORY_GEO = { label: COUNTRIES.US?.label, id: COUNTRIES.US?.id, cc: COUNTRIES.US?.cc };

const getStatementOperand = (statement) => (Array.isArray(statement) ? statement[0] : 'none');

const isAndStatement = (statement) => getStatementOperand(statement) === 'and';
const isNotStatement = (statement) => getStatementOperand(statement) === 'not';
const isNoneStatement = (statement) => getStatementOperand(statement) === 'none';

function optimizeStatement(statement) {
  // [and, filter1, [and, filter2, filter3]] => [and, filter1, filter2, filter3]
  if (isNoneStatement(statement)) return statement;

  if (isAndStatement(statement)) {
    if (statement.length === 2 && !isNoneStatement(statement[1])) return statement[1];
    let optimizedLogicStatement = ['and'];
    statement.slice(1).forEach(function (s) {
      if (isAndStatement(s)) {
        optimizedLogicStatement = optimizedLogicStatement.concat(s.slice(1));
      } else {
        optimizedLogicStatement.push(optimizeStatement(s));
      }
    });
    return optimizedLogicStatement;
  }

  for (let i = 0; i < statement.length; i++) {
    statement[i] = optimizeStatement(statement[i]);
  }

  return statement;
}

function convertAppsSegmentToLogicStatement(apps) {
  const filterType = 'app';
  let filters = _.pick(apps, ['required', 'included', 'excluded']);
  filters = _.mapValues(filters, (filterVal, filterType) => _.map(filterVal, 'id'));
  filters = _.mapValues(filters, (filterVal) => _.map(filterVal, (val) => ({ [filterType]: val.toLowerCase() })));

  const logicStatement = [];
  if (filters['required']) logicStatement.concat(filters['required']);
  if (filters['included']) logicStatement.push(['or'].concat(filters['included']));
  if (filters['excluded']) logicStatement.push(['not', ['or'].concat(filters['excluded'])]);

  return logicStatement.length === 1 ? logicStatement[0] : ['and'].concat(logicStatement);
}

function convertInterestsAndWebsitesSegmentToLogicStatement(interests) {
  const filterType = interests.type === 'interests' ? interests.levelOfIntent.value : 'domains';
  let filters = _.pick(interests, ['required', 'included', 'excluded']);
  filters = _.mapValues(filters, (filterVal, filterType) => _.map(filterVal, 'id'));
  filters = _.mapValues(filters, (filterVal) =>
    _.map(filterVal, (val) => ({ [filterType]: filterType === 'domains' ? val.toLowerCase() : val }))
  );
  let logicStatement = [];
  if (filters['required'] && filters['required'].length > 0)
    logicStatement = logicStatement.concat(filters['required']);
  if (filters['included'] && filters['included'].length > 0) logicStatement.push(['or'].concat(filters['included']));
  if (filters['excluded'] && filters['excluded'].length > 0)
    logicStatement.push(['not', ['or'].concat(filters['excluded'])]);

  return logicStatement.length === 1 ? logicStatement[0] : ['and'].concat(logicStatement);
}

function convertInterestsToLogicStatement(interests) {
  const levelOfIntentKey = LEVEL_OF_INTENT_MAP[interests.levelOfIntent.value];
  return convertInterestsAndWebsitesSegmentToLogicStatement(
    _.extend({}, interests, { levelOfIntent: { value: levelOfIntentKey } })
  );
}

function convertTvShowsToLogicStatement(tvShows, channel) {
  let filters = _.pick(tvShows, TV_SHOWS_FIELDS);
  filters = _.mapKeys(filters, (filterVal, filterType) =>
    filterType === 'networks'
      ? channel === CHANNELS.articles?.name
        ? 'networks'
        : TV_SHOWS_MAPPING_MAP.NETWORK
      : filterType
  );
  filters = _.mapValues(filters, (filterVal, filterType) => _.map(filterVal, 'value'));
  filters = _.map(filters, function (filterVal, filterType) {
    let values = _.map(filterVal, (v) => ({ [filterType]: v }));
    return values.length === 1 ? values[0] : ['or'].concat(values);
  });
  return filters.length === 1 ? filters[0] : ['and'].concat(filters);
}

function getFilterMappingWithShowAndSeriesIds(filterMapping, tvShows) {
  if (!_.isEmpty(tvShows.tv)) {
    const tvShowsByIsTypeSeries = _.groupBy(tvShows.tv, (show) =>
      show.type === 'series' ? ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES : ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW
    );
    for (let key in tvShowsByIsTypeSeries) {
      filterMapping[key] = tvShowsByIsTypeSeries[key];
    }
  }
  return filterMapping;
}

function getFilterMappingWithGenreIds(filterMapping, tvShows) {
  if (!_.isEmpty(tvShows.genres)) {
    filterMapping[ADVANCED_TV_SHOWS_MAPPING_MAP.GENRE] = tvShows.genres;
  }
  return filterMapping;
}

function convertTvShowsToNewLogicStatement(tvShows, channel) {
  let filters = _.pick(tvShows, NEW_QUERY_TV_SHOWS_FIELDS);
  filters = _.mapValues(filters, (filterVal, filterType) => _.map(filterVal, 'value'));
  filters = _.map(filters, function (filterVal, filterType) {
    let values = _.map(filterVal, (v) => ({ [filterType]: v }));
    return values.length === 1 ? values[0] : ['or'].concat(values);
  });

  let filterMapping = _.pick(tvShows, Object.keys(TV_SHOWS_FILTER_MAPPING_MAP));
  filterMapping = _.mapKeys(filterMapping, (filterVal, filterType) => TV_SHOWS_FILTER_MAPPING_MAP[filterType]);
  filterMapping = getFilterMappingWithShowAndSeriesIds(filterMapping, tvShows);
  filterMapping = getFilterMappingWithGenreIds(filterMapping, tvShows);
  filterMapping = _.mapValues(filterMapping, (filterVal, filterType) => _.flatMap(filterVal, 'value'));
  const isFilterMappingEmpty = _.isEmpty(filterMapping) || !_.some(filterMapping, (v) => !_.isEmpty(v));

  if (isSmartTvChannel(channel)) addDateRangeToFilterMapping(filterMapping, tvShows);
  if (filters.length > 0 && !isFilterMappingEmpty)
    return ['and', filters[0], { 'place-holder': convertTvShowsSegmentToMd5(channel, filterMapping) }];
  if (filters.length > 0) return filters[0];
  if (!isFilterMappingEmpty) return { 'place-holder': convertTvShowsSegmentToMd5(channel, filterMapping) };
}

function convertAdvancedTvShowsToLogicStatement(tvShows, channel) {
  const filterMapping = getAdvancedTvShowsFilterMapping(tvShows, channel);
  // Only attach the TV constraint when the source actually has a selection (shows/networks/genres).
  // A source with no selection contributes nothing here, so the query runs on the shared non-TV parts.
  if (hasAdvancedTvSelections(filterMapping))
    return { 'place-holder': convertAdvancedTvShowsSegmentToMd5(channel, filterMapping) };
}

function convertCommercialsSegmentToLogicStatement(commercials, channel) {
  let filters = _.pickBy(commercials, (value, key) => {
    if (commercials.queryKeys && COMMERCIALS_QUERY_KEY_FIELDS.includes(key)) {
      return commercials.queryKeys.includes(key);
    }
    return Object.keys(COMMERCIALS_DESC_MAP).includes(key);
  });

  filters = _.mapKeys(filters, (filterVal, filterType) => COMMERCIALS_DESC_MAP[filterType]);
  filters = _.mapValues(filters, (filterVal, filterType) => {
    if (COMMERCIALS_ID_LIST_FIELDS.includes(filterType)) {
      if (filterType === COMMERCIALS_MAPPING_MAP.EXPOSURE && isAllLevelsOfExposure(filterVal)) {
        return [];
      }
      return [...new Set(_.flatMap(filterVal, 'value'))];
    }
    if (filterType === 'table-name' && CHANNELS[channel]?.tablePrefix) {
      return `${CHANNELS[channel]?.tablePrefix}_ads`;
    }
    return filterVal;
  });

  //filters['type'] = (isAdvancedTvChannel(channel)) ? "tv-advanced" : undefined;
  filters['type'] = channel === CHANNELS.hisense?.name ? 'tv-advanced' : 'tv'; // temp change for Dima
  if (isSmartTvChannel(channel)) addDateRangeToFilterMapping(filters, commercials);
  if (Object.keys(filters).length === 0) return {};
  filters['viewing-type'] = commercials.viewingType;
  return { 'place-holder': convertCommercialsSegmentToMd5(channel, filters) };
}

function replaceValues(values, oldValue, newValues) {
  if (!_.includes(values, oldValue)) return;
  values.splice(values.indexOf(oldValue), 1);
  _.each(newValues, (newVal) => values.push(newVal));
}

function rebuildIncome(income) {
  let newIncome = [];
  _.each(income, (val) => (_.isArray(val) ? _.each(val, (vv) => newIncome.push(vv)) : newIncome.push(val)));
  return newIncome;
}

function convertDemographicsSegmentToLogicStatement(
  demographics,
  channel,
  allPermittedGeos,
  isBidStream,
  isDeterministic
) {
  let filters = _.pick(demographics, DEMOGRAPHICS_FIELDS);

  filters = _.mapValues(filters, function (filterVal, filterType) {
    if (_.includes(['age', 'gender'], filterType))
      return _.map(filterVal, 'summary').map((f) => (f === '13-17' ? '12-17' : f));
    if (_.includes(['children'], filterType)) return _.map(filterVal, (children) => children.label.toLowerCase());
    if (_.includes(['ethnicity', 'income', 'states'], filterType)) return _.map(filterVal, 'value');
    if (_.includes(['geo'], filterType))
      return geoByChannel(filterVal, channel, allPermittedGeos, isBidStream, isDeterministic);
  });
  replaceValues(filters.income, '0-25k', ['0-15k', '15-25k']);
  replaceValues(filters.income, '25-50k', ['25-35k', '35-50k']);
  if (filters.income) {
    // Flatten income params - to accommodate server api
    filters.income = rebuildIncome(filters.income);
  }

  filters = _.mapKeys(filters, function (val, key) {
    if (key === 'ethnicity') return CHANNELS[channel]?.isSgTelco ? 'sg-races' : 'new-races';
    return KEY_NAME_MAP[key] || key;
  });
  filters = _.map(filters, function (filterVal, filterType) {
    const values = _.map(filterVal, (v) => {
      const type = filterType == 'ages' && SPECIFIC_AGES.includes(v) ? 'specific-ages' : filterType;
      return { [type]: v };
    });
    return values.length == 1 ? values[0] : ['or'].concat(values);
  });
  return filters.length == 1 ? filters[0] : ['and'].concat(filters);
}

function convertSegmentToLogicStatement(segment, channel, allPermittedGeos, isBidStream, isDeterministic) {
  if (DEMOGRAPHICS_SEGMENT_TYPES.includes(segment.type))
    return convertDemographicsSegmentToLogicStatement(segment, channel, allPermittedGeos, isBidStream, isDeterministic);
  if (segment.type === 'interests') return convertInterestsToLogicStatement(segment);
  if (segment.type === 'websites') return convertInterestsAndWebsitesSegmentToLogicStatement(segment);
  if (segment.type === 'lifestyle') {
    return { intentions: segment.value };
  }
  if (segment.type === 'tvShows' && isSmartTvChannel(channel))
    return convertTvShowsToNewLogicStatement(segment, channel);
  if (segment.type === 'tvShows') return convertTvShowsToLogicStatement(segment, channel);
  if (segment.type === 'advancedTvShows') return convertAdvancedTvShowsToLogicStatement(segment, channel);
  if (segment.type === '1st party') return { 'place-holder': String(segment.value) };
  if (segment.type === 'apps') return convertAppsSegmentToLogicStatement(segment);
  if (segment.type === 'smartTvCommercials') return convertCommercialsSegmentToLogicStatement(segment, channel);
}

function convertAudienceSegmentToFilterMapping(audienceSegment, channel) {
  const filterMapping = audienceSegment.reduce((filterMapping, segment) => {
    if (segment.type === 'smartTvCommercials')
      return { ...filterMapping, ...convertCommercialsSegmentToFilterMapping(segment, channel) };

    if (segment.type === 'tvShows' && isSmartTvChannel(channel) && ['networks', 'tv'].some((k) => k in segment))
      return { ...filterMapping, ...convertTvShowsSegmentToFilterMapping(segment, channel) };

    if (
      segment.type === 'advancedTvShows' &&
      TV_SHOWS_FIELDS.concat(_.keys(ADVANCED_TV_SHOWS_FILTER_MAPPING_MAP)).some((k) => k in segment)
    )
      return { ...filterMapping, ...convertAdvancedTvShowsSegmentToFilterMapping(segment, channel) };

    if (segment.type === '1st party') return { ...filterMapping, ...convertFirstPartySegmentToFilterMapping(segment) };

    return filterMapping;
  }, {});
  return _.isEmpty(filterMapping) ? null : filterMapping;
}

function convertCommercialsSegmentToFilterMapping(commercials, channel) {
  let filters = _.pickBy(commercials, (value, key) => {
    if (commercials.queryKeys && COMMERCIALS_QUERY_KEY_FIELDS.includes(key))
      return commercials.queryKeys.includes(key);
    return Object.keys(COMMERCIALS_DESC_MAP).includes(key);
  });

  filters = _.mapKeys(filters, (filterVal, filterType) => COMMERCIALS_DESC_MAP[filterType]);

  const filterMapping = _.mapValues(filters, (filterVal, filterType) => {
    if (COMMERCIALS_ID_LIST_FIELDS.includes(filterType)) {
      if (filterType === COMMERCIALS_MAPPING_MAP.EXPOSURE && isAllLevelsOfExposure(filterVal)) return [];
      return [...new Set(_.flatMap(filterVal, 'value'))];
    }
    if (filterType === 'table-name' && CHANNELS[channel]?.tablePrefix) {
      return `${CHANNELS[channel]?.tablePrefix}_ads`;
    }
    return filterVal;
  });

  //filterMapping['type'] = (isAdvancedTvChannel(channel)) ? "tv-advanced" : undefined;
  filterMapping['type'] = channel === CHANNELS.hisense?.name ? 'tv-advanced' : 'tv'; // temp change for Dima
  if (isSmartTvChannel(channel)) addDateRangeToFilterMapping(filterMapping, commercials);
  filterMapping['viewing-type'] = commercials.viewingType;
  return { [convertCommercialsSegmentToMd5(channel, filterMapping)]: filterMapping };
}

function convertTvShowsSegmentToFilterMapping(tvShows, channel) {
  let filters = _.pick(tvShows, Object.keys(TV_SHOWS_FILTER_MAPPING_MAP));
  filters = _.mapKeys(filters, (filterVal, filterType) => TV_SHOWS_FILTER_MAPPING_MAP[filterType]);
  filters = getFilterMappingWithShowAndSeriesIds(filters, tvShows);

  const filterMapping = _.mapValues(filters, (filterVal, filterType) => {
    if (
      [
        TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES,
        TV_SHOWS_MAPPING_MAP.SHOW,
        TV_SHOWS_MAPPING_MAP.NETWORK,
        TV_SHOWS_MAPPING_MAP.GENRE,
        TV_SHOWS_MAPPING_MAP.EXPOSURE,
      ].includes(filterType)
    ) {
      if (filterType === TV_SHOWS_MAPPING_MAP.EXPOSURE && isAllLevelsOfExposure(filterVal)) return [];
      return _.flatMap(filterVal, 'value');
    }
    return filterVal;
  });
  if (CHANNELS[channel]?.tablePrefix) {
    filterMapping['table-name'] = `${CHANNELS[channel]?.tablePrefix}_media`;
  }
  if (isSmartTvChannel(channel)) addDateRangeToFilterMapping(filterMapping, tvShows);

  return { [convertTvShowsSegmentToMd5(channel, filterMapping)]: filterMapping };
}

function getAdvancedTvShowsFilterMapping(tvShows, channel) {
  let filters = _.pick(tvShows, Object.keys(ADVANCED_TV_SHOWS_FILTER_MAPPING_MAP));
  filters = _.mapKeys(filters, (filterVal, filterType) => ADVANCED_TV_SHOWS_FILTER_MAPPING_MAP[filterType]);
  // Unified TV re-channels each per-source preview/activation call to hisense/peerlogix; each
  // call must carry only that source's selections. Items picked in a single-source channel carry
  // no source tag and always pass.
  if (UNIFIED_TV_SOURCES.some((tvSource) => tvSource.value === channel)) {
    [ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES, ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW].forEach((filterType) => {
      if (!filters[filterType]) return;
      filters[filterType] = _.filter(filters[filterType], (item) => !item.source || item.source === channel);
    });
  }
  if (filters[ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES]) {
    const filteredMovies = _.filter(filters[ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES], (show) => show.is_movie);
    if (!filters[ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW]) {
      filters[ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW] = [];
    }
    filters[ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW] = filters[ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW].concat(filteredMovies);
    filters[ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES] = _.filter(
      filters[ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES],
      (show) => !filteredMovies.includes(show)
    );
  }

  const filterMapping = _.mapValues(filters, (filterVal, filterType) => {
    if (
      [
        ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES,
        ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW,
        ADVANCED_TV_SHOWS_MAPPING_MAP.EXPOSURE,
      ].includes(filterType)
    ) {
      if (filterType === ADVANCED_TV_SHOWS_MAPPING_MAP.EXPOSURE && isAllLevelsOfExposure(filterVal)) return [];
      // The same id can be selected under both unified TV sources — send it once.
      return _.uniq(_.map(filterVal, 'value'));
    }
    if (filterType === ADVANCED_TV_SHOWS_MAPPING_MAP.NETWORK) {
      return _.map(filterVal, 'label');
    }
    if (filterType === ADVANCED_TV_SHOWS_MAPPING_MAP.VIEWERSHIP_MODES) {
      return _.flatten(_.map(filterVal, 'value'));
    }
    if (filterType === ADVANCED_TV_SHOWS_MAPPING_MAP.QUALIFIED_CAPPING) {
      return filterVal.value;
    }
    if (filterType === ADVANCED_TV_SHOWS_MAPPING_MAP.GENRE) {
      return _.flatten(_.map(filterVal, 'genres'));
    }
    return filterVal;
  });
  filterMapping['type'] = CHANNELS[channel]?.advancedTvTypes?.[tvShows.viewingType];
  filterMapping['viewing-type'] = tvShows.viewingType;
  filterMapping['table-name'] = `${CHANNELS[channel]?.tablePrefix}_media`;
  addDateRangeToFilterMapping(filterMapping, tvShows);
  if (tvShows.minViewedProgs) {
    filterMapping[ADVANCED_TV_SHOWS_MAPPING_MAP.NUMBER_OF_OBJECTS] = { min: tvShows.minViewedProgs };
  }

  return filterMapping;
}

function convertAdvancedTvShowsSegmentToFilterMapping(tvShows, channel) {
  const filterMapping = getAdvancedTvShowsFilterMapping(tvShows, channel);
  // Mirror convertAdvancedTvShowsToLogicStatement: a source with no selection contributes no
  // filter-mapping entry, so we never leave a broken (empty-ids) or dangling entry for the query.
  if (!hasAdvancedTvSelections(filterMapping)) return {};
  return { [convertAdvancedTvShowsSegmentToMd5(channel, filterMapping)]: filterMapping };
}

function convertFirstPartySegmentToFilterMapping(segment) {
  const isNotNullValue = (val) => val && val !== 'null';
  const hasPresetFile = isNotNullValue(segment.s3_bucket) && isNotNullValue(segment.s3_path);
  // Temporary solution while waiting for Oren's team to convert old segments to the new format.
  // Once conversion is complete, we can remove the conditional logic and move to the new format.
  const filterMapping =
    segment.first_party_source_id === FIRST_PARTY_SOURCE_ENUM.DMP
      ? {
          type: 'first-party',
          'fp-id': segment.value,
        }
      : {
          type: 'file',
          bucket: hasPresetFile ? segment.s3_bucket : 'kona-turn',
          'file-key': hasPresetFile ? segment.s3_path : `bidstream-1st-party-audience/${segment.value}`,
        };
  return { [String(segment.value)]: filterMapping };
}

function hasSegmentTimeframe(segment) {
  return segment.startDate && segment.endDate;
}

function hasFilterMappingTimeframe(filterMapping) {
  return filterMapping[COMMON_MAPPING_MAP.START_TIME] && filterMapping[COMMON_MAPPING_MAP.END_TIME];
}

function getTimeframePlaceholder(filters) {
  return `_${filters[COMMON_MAPPING_MAP.START_TIME]}-${filters[COMMON_MAPPING_MAP.END_TIME]}`;
}

function addDateRangeToFilterMapping(filterMapping, segment) {
  if (!hasSegmentTimeframe(segment)) return;

  const formatDate = (dateString) => moment(new Date(dateString)).format('YYYY-MM-DD');
  if (segment.timeframe) {
    const [value, unit] = segment.timeframe.split(' ');
    filterMapping[COMMON_MAPPING_MAP.START_TIME] = moment().subtract(value, unit).format('YYYY-MM-DD');
    filterMapping[COMMON_MAPPING_MAP.END_TIME] = moment().format('YYYY-MM-DD');
  } else {
    filterMapping[COMMON_MAPPING_MAP.START_TIME] = formatDate(segment.startDate);
    filterMapping[COMMON_MAPPING_MAP.END_TIME] = formatDate(segment.endDate);
  }
}

function convertCommercialsSegmentToMd5(channel, commercials) {
  let commercialsStr = `commercials_${channel}`;
  _.forEach(commercials, (value, key) => {
    const mdValue = Array.isArray(value) ? _.join(value.sort(), '_') : value;
    commercialsStr += `_${key}_${mdValue}`;
  });
  if (hasFilterMappingTimeframe(commercials)) commercialsStr += getTimeframePlaceholder(commercials);
  commercialsStr += `_${_.join((commercials['exposure'] || []).sort(), '_')}`;
  return md5(commercialsStr);
}

function convertTvShowsSegmentToMd5(channel, tvShows) {
  let tvShowsStr = `tv_${channel}_${_.join((tvShows[TV_SHOWS_MAPPING_MAP.SHOW] || []).sort(), '_')}_${_.join(
    (tvShows[TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES] || []).sort(),
    '_'
  )}_${_.join((tvShows[TV_SHOWS_MAPPING_MAP.NETWORK] || []).sort(), '_')}_${_.join(
    (tvShows[TV_SHOWS_MAPPING_MAP.GENRE] || []).sort(),
    '_'
  )}_${_.join((tvShows[TV_SHOWS_MAPPING_MAP.EXPOSURE] || []).sort(), '_')}`;
  if (hasFilterMappingTimeframe(tvShows)) tvShowsStr += getTimeframePlaceholder(tvShows);
  return md5(tvShowsStr);
}

function convertAdvancedTvShowsSegmentToMd5(channel, tvShows) {
  let tvShowsStr = `tv_${channel}_${tvShows[ADVANCED_TV_SHOWS_MAPPING_MAP.TYPE]}_${_.join(
    (tvShows[ADVANCED_TV_SHOWS_MAPPING_MAP.SHOW] || []).sort(),
    '_'
  )}_${_.join((tvShows[ADVANCED_TV_SHOWS_MAPPING_MAP.PROGRAM_SERIES] || []).sort(), '_')}_${_.join(
    (tvShows[ADVANCED_TV_SHOWS_MAPPING_MAP.NETWORK] || []).sort(),
    '_'
  )}_${_.join((tvShows[ADVANCED_TV_SHOWS_MAPPING_MAP.GENRE] || []).sort(), '_')}_${_.join(
    (tvShows[ADVANCED_TV_SHOWS_MAPPING_MAP.EXPOSURE] || []).sort(),
    '_'
  )}_${_.join((tvShows[ADVANCED_TV_SHOWS_MAPPING_MAP.VIEWERSHIP_MODES] || []).sort(), '_')}_${
    tvShows[ADVANCED_TV_SHOWS_MAPPING_MAP.QUALIFIED_CAPPING]
  }_${JSON.stringify(tvShows[ADVANCED_TV_SHOWS_MAPPING_MAP.NUMBER_OF_OBJECTS] || {})}`;
  if (hasFilterMappingTimeframe(tvShows)) tvShowsStr += getTimeframePlaceholder(tvShows);
  return md5(tvShowsStr);
}

function convertAudienceSegmentToLogicStatement(
  audienceSegment,
  channel,
  allPermittedGeos,
  isTv = null,
  isBidStreamChannel = null,
  isDeterministic = null
) {
  audienceSegment = _.cloneDeep(audienceSegment);
  _.each(audienceSegment, (segment) => (segment.operand = segment.operand || { value: 'and' }));

  // When user "exclude" demographics with country, delete all geos from all segments and add an "require" segment with this geo
  const geo = _.get(
    _.filter(audienceSegment, (segment) => DEMOGRAPHICS_SEGMENT_TYPES.includes(segment.type))[0],
    'geo'
  );

  _.each(audienceSegment, (segment) => delete segment.geo);
  _.remove(
    audienceSegment,
    (segment) => DEMOGRAPHICS_SEGMENT_TYPES.includes(segment.type) && _.isEmpty(_.pick(segment, DEMOGRAPHICS_FIELDS))
  );
  audienceSegment.push({ geo, type: 'demographics', operand: { value: 'and' } });

  const segmentByOperand = _.groupBy(audienceSegment, 'operand.value');
  // For bidstream channel, always use bidstream data; also use bidstream when 1st party segment is present
  const isBidStream =
    channel === CHANNELS.bidstream?.name || isBidStreamChannel || _.some(audienceSegment, { type: '1st party' });

  let andStatements = _.map(segmentByOperand['and'], function (segment) {
    return convertSegmentToLogicStatement(segment, channel, allPermittedGeos, isBidStream, isDeterministic);
  });
  let orStatements = _.map(segmentByOperand['or'], function (segment) {
    return convertSegmentToLogicStatement(segment, channel, allPermittedGeos, isBidStream, isDeterministic);
  });
  let notStatements = _.map(segmentByOperand['not'], function (segment) {
    return convertSegmentToLogicStatement(segment, channel, allPermittedGeos, isBidStream, isDeterministic);
  });

  if (orStatements.length) orStatements = [['or'].concat(orStatements)];
  if (notStatements.length)
    notStatements = [['not'].concat(notStatements.length === 1 ? notStatements : [['or'].concat(notStatements)])];

  let logicalStatement = ['and'].concat(andStatements).concat(orStatements).concat(notStatements);
  if ((isTv || _.some(audienceSegment, { type: 'tvShows' })) && !CHANNELS[channel]?.isTv)
    logicalStatement.push({ 'any-tv': 'yes' });

  return optimizeStatement(logicalStatement);
}

function geoByChannel(geo, channel, allPermittedGeos = null, isBidStream = null, isDeterministic = null) {
  // PHOB-1857: unified_tv has no backend table/geo contract of its own — it must be queried per real
  // source (hisense/peerlogix). Reaching here with 'unified_tv' would silently fall through to the
  // generic tail and build a non-existent `unified_tv_media` table with a plain-country geo. Fail loud
  // so a future caller that forgets the per-source fan-out is caught immediately rather than silently
  // mis-gating the Activate button.
  if (channel === CHANNELS.unified_tv?.name) {
    throw new Error('geoByChannel: unified_tv must be queried per source (hisense/peerlogix), not directly');
  }
  if (channel === CHANNELS.data_spark?.name) return [`sg:${CHANNELS.data_spark?.tablePrefix}_https`];
  if (channel === CHANNELS.sg_bidstream?.name) return ['sg:bid_stream'];
  if (CHANNELS[channel]?.isSgTelco) return [`sg:${channel}`];
  if (channel === CHANNELS.smart_tv_inscape?.name)
    return compact([
      `us:bid_stream_${CHANNELS.smart_tv_inscape?.tablePrefix}`,
      isDeterministic && `us:${CHANNELS.smart_tv_inscape?.tablePrefix}`,
    ]);
  if ([CHANNELS.tivo?.name, CHANNELS.peerlogix?.name].includes(channel)) {
    return [`us:bid_stream_${CHANNELS[channel]?.tablePrefix}`];
  }
  if (channel === CHANNELS.hisense?.name) {
    const geoPrefix = ((geo || [])[0]?.cc || COUNTRIES.US?.cc).toLowerCase();
    return [`${geoPrefix}:bid_stream_${CHANNELS.hisense?.tablePrefix}`];
  }
  if (channel === CHANNELS.au_telco?.name) return ['au:optus'];

  // For bidstream channel, always use :bid_stream suffix
  const isBidStreamChannel = channel === CHANNELS.bidstream?.name || isBidStream;
  const channelAddition = isBidStreamChannel ? ':bid_stream' : '';
  const geos = _.isEmpty(geo) ? allPermittedGeos : geo;
  return _.map(geos, (geoRecord, key) => geoRecord.cc.toLowerCase() + channelAddition);
}

function parseToNumber(str) {
  return _.toNumber(_.replace(str, /\D/g, ''));
}

function mergeRangesForSummary(ranges) {
  if (_.isEmpty(ranges)) return [];
  ranges = _.sortBy(ranges);
  const START = 0,
    END = 1;
  let mergedRanges = [];
  let currentRange = _.split(ranges[START], '-');

  if (currentRange.length === 1) currentRange.push(currentRange[START]);
  for (let i = 1; i < ranges.length; i++) {
    let nextRange = _.split(ranges[i], '-');

    if (nextRange.length === 1) nextRange.push(nextRange[START]);
    if (
      _.isEqual(parseToNumber(currentRange[END]), parseToNumber(nextRange[START])) ||
      _.isEqual(parseToNumber(currentRange[END]) + 1, parseToNumber(nextRange[START]))
    ) {
      currentRange = [currentRange[START], nextRange[END]];
    } else {
      mergedRanges.push(currentRange);
      currentRange = [nextRange[START], nextRange[END]];
    }
  }
  mergedRanges.push(currentRange);

  return _.map(mergedRanges, (range) => _.uniq(range));
}

function getRangeValueSummary(ranges, rangeText) {
  const mergedRanges = mergeRangesForSummary(_.map(ranges, (range) => range.label));
  return (
    '<ii>' +
    rangeText +
    ' between</ii> ' +
    _.join(
      _.map(mergedRanges, (range) => _.join(range, '-')),
      ' and '
    )
  );
}

function getIncomeValueSummary(income, filtersPartition) {
  const has_first_income = _.isEqual(income[0].value, filtersPartition.newIncome[0].value);
  const has_last_income = _.isEqual(
    income[income.length - 1].value,
    filtersPartition.newIncome[filtersPartition.newIncome.length - 1].value
  );
  let summary = '<ii>Income ';
  if (!has_first_income && !has_last_income) {
    summary += 'between</ii> ' + income[0].label.split('-')[0] + '-' + income[income.length - 1].label.split('-')[1];
  } else if (has_first_income) {
    summary += 'below</ii> ' + income[income.length - 1].label.split('-')[1];
  } else {
    summary += 'over</ii> ' + income[0].label.split('-')[0] + 'K';
  }

  summary = summary.replace(/\+k/i, '').replace(/99k/i, '100K');
  return summary;
}

function multipleItemsSummary(items, concatenateSymbol, itemsToDisplay = 1) {
  const arrOfItems = [...items];
  const extra = arrOfItems.length - itemsToDisplay > 0 ? arrOfItems.length - itemsToDisplay : 0;
  const labels = arrOfItems.map((item) => (_.isString(item) ? item : item.label));
  let summary = labels.slice(0, itemsToDisplay).join(` <ii>${concatenateSymbol}</ii> `);

  if (extra) {
    const extraItems = labels.slice(itemsToDisplay, labels.length).join(` ${concatenateSymbol} `);
    summary += `<span title="${extraItems}" am-tooltip="top center to bottom center"> <ii>${concatenateSymbol}</ii> ${extra} other${
      extra === 1 ? '' : 's'
    } </span>`;
  }

  return summary;
}

function getDemographicsValueSummary(demographics, filtersPartition) {
  let summary = [];
  if (demographics.gender) summary.push(_.upperFirst(demographics.gender[0].label));
  if (demographics.age) summary.push(getRangeValueSummary(demographics.age, 'Age'));
  if (demographics.income) summary.push(getIncomeValueSummary(demographics.income, filtersPartition));
  if (demographics.children) summary.push(_.upperFirst(demographics.children[0].summary));
  if ((demographics.geo && demographics.geo[0]?.label) || !_.isEmpty(demographics.states))
    summary.push(getGeoSummary((demographics.geo || [])[0]?.label, demographics.states));
  if (demographics.ethnicity && demographics.ethnicity.length)
    summary.push(demographics.ethnicity.map((e) => e.label).join(OR_DELIMITER));
  return summary.join(', ');
}

function getGeoSummary(geoLabel, states) {
  const geoSummary = geoLabel || '';
  let summary = geoSummary;
  let statesSummary;
  if (!_.isEmpty(states)) {
    if (states.length > 3) {
      statesSummary = `${states[0].value}, ${states[1].value} & ${states.length - 2} others`;
    } else if (states.length > 1) {
      statesSummary = `${_.map(states.slice(0, states.length - 1), 'value').join(', ')} & ${_.last(states).value}`;
    } else {
      statesSummary = states[0].value;
    }
  }
  if (statesSummary) {
    summary += geoSummary ? ` (${statesSummary})` : statesSummary;
  }
  return summary;
}

function getInterestsAndWebsitesValueSummary(interests) {
  let summary = [];
  if (interests.required && interests.required.length > 0)
    summary = summary.concat(_.map(interests.required, 'text').join(AND_DELIMITER));
  if (interests.included && interests.included.length > 0)
    summary = summary.concat(_.map(interests.included, 'text').join(OR_DELIMITER));
  if (interests.excluded && interests.excluded.length > 0)
    summary = summary.concat(NOT_DELIMITER + _.map(interests.excluded, 'text').join(OR_DELIMITER));
  if (interests.levelOfIntent) summary = summary.concat('<ii>Level of Intent</ii> ' + interests.levelOfIntent.label);
  return summary.join(',&nbsp;&nbsp;&nbsp;');
}

function getLinkedinDemographicsValueSummary(demographics) {
  let summary = [];
  if (demographics.gender) summary.push(_.upperFirst(demographics.gender[0].label));
  if (demographics.age) summary.push(getRangeValueSummary(demographics.age, 'Age'));
  if (demographics.country) summary.push(multipleItemsSummary(demographics.country, 'and'));
  if (demographics.state) summary.push(multipleItemsSummary(demographics.state, 'and'));
  if (demographics.regions) summary.push(multipleItemsSummary(demographics.regions, 'and'));

  return summary.join(', ');
}

function getLinkedinIndustriesValueSummary(industries) {
  let summary = [];
  if (industries.industries) {
    industries.industries.forEach((industry) => summary.push(industry.label));
  }
  return summary.join(', ');
}

function getLinkedinCompaniesValueSummary(companies) {
  let summary = [];
  if (companies.sizes) summary.push(getRangeValueSummary(companies.sizes, 'Company size'));
  if (companies.names) summary = summary.concat(_.map(companies.names, 'text').join(OR_DELIMITER));
  return summary.join(', ');
}

function getLinkedinJobsValueSummary(jobs) {
  let summary = [];
  if (jobs.seniorities) jobs.seniorities.forEach((seniority) => summary.push(seniority.label));
  if (jobs.functions) jobs.functions.forEach((func) => summary.push(func.label));
  if (jobs.jobTitles) jobs.jobTitles.forEach((jobTitle) => summary.push(jobTitle.label));
  return summary.join(', ');
}

function getTvShowsSummary(tvShows, isAudienceInfoTooltip = false) {
  let summary = [];
  if (tvShows.networks)
    summary = summary.concat('<ii>Networks</ii> ' + multipleItemsSummary(tvShows.networks, 'or', 3));
  if (tvShows.tv) summary = summary.concat('<ii>Watched either</ii> ' + multipleItemsSummary(tvShows.tv, 'or', 2));
  if (tvShows.episodes)
    summary = summary.concat('<ii>Watched either</ii> ' + multipleItemsSummary(tvShows.episodes, 'or', 2));
  if (!_.isEmpty(tvShows.exposure) && tvShows.exposure.length < LEVELS_OF_EXPOSURE.length) {
    summary = summary.concat('<ii>Level of Exposure</ii> ' + _.join(_.map(tvShows.exposure, 'label'), ', '));
  }
  if (tvShows.genres) summary = summary.concat('<ii>Genres</ii> ' + multipleItemsSummary(tvShows.genres, 'or', 3));
  if (isAudienceInfoTooltip && tvShows.startDate && tvShows.endDate)
    summary = summary.concat('<ii>Timeframe</ii> ' + tvShows.startDate + ' - ' + tvShows.endDate);
  if (!_.isEmpty(tvShows.viewershipModes))
    summary = summary.concat(
      '<ii>Viewership Modes</ii> ' + multipleItemsSummary(_.map(tvShows.viewershipModes, 'label'), 'or', 2)
    );
  if (tvShows.viewEventQual)
    summary = summary.concat('<ii>Viewing Event Qualifier</ii> ' + tvShows.viewEventQual.label);
  if (tvShows.minViewedProgs) {
    summary = summary.concat('<ii>Minimum Number of Viewed Programs</ii> ' + tvShows.minViewedProgs);
  }
  return summary.join(', ');
}

function getCommercialsValueSummary(commercials, isAudienceInfoTooltip = false) {
  let summary = [];
  let combinations = [];
  const itemsToDisplay = 1;
  forEach(commercials.creatives, (creative) => {
    let combination = `${creative.brand_label}, ${creative.creative_label}`;
    if (creative.brand_parent_label) combination = creative.brand_parent_label + ', ' + combination;
    combinations.push(combination);
  });
  if (commercials.creatives)
    summary = summary.concat('<ii>Was exposed to</ii> ' + multipleItemsSummary(combinations, 'or', itemsToDisplay));
  if (!_.isEmpty(commercials.exposure) && commercials.exposure.length < LEVELS_OF_EXPOSURE.length) {
    summary = summary.concat('<ii>Level of Exposure</ii> ' + _.join(_.map(commercials.exposure, 'label'), ', '));
  }
  if (isAudienceInfoTooltip && commercials.startDate && commercials.endDate)
    summary = summary.concat('<ii>Timeframe</ii> ' + commercials.startDate + ' - ' + commercials.endDate);
  return summary.join(', ');
}

function getFirstPartyValueSummary(segment) {
  const { label } = segment;
  // Only show segment label - expiration info is displayed separately on the right side
  return label;
}

// PHOB-1857: a unified TV segment carries shows from two sources (ACR/hisense, Streaming/peerlogix),
// tagged per item via `source`. In the builder we always show one summary row per source — the source
// name plus that source's shows and the shared fields (networks/genres/qualifier) — divided by a plain
// line. We iterate the two fixed sources (not segment.sources), so a source with no selected shows still
// gets its named row. Networks/genres are shared across both sources, so each source row echoes them
// (deduplicated per source). A source's values render when it has its own shows/episodes OR there is
// shared content (networks/genres); otherwise the row is empty (name only) — a lone shared qualifier
// does not populate an otherwise-empty source.
function getUnifiedTvShowsSummary(segment) {
  const hasSharedContent = !_.isEmpty(segment.networks) || !_.isEmpty(segment.genres);
  return UNIFIED_TV_SOURCES.map(({ value: sourceValue, label: sourceLabel }) => {
    const sourceTv = (segment.tv || []).filter((item) => item.source === sourceValue);
    const sourceEpisodes = (segment.episodes || []).filter((item) => item.source === sourceValue);
    const hasSourceData = sourceTv.length || sourceEpisodes.length || hasSharedContent;
    const perSourceSegment = {
      ...segment,
      tv: sourceTv.length ? sourceTv : undefined,
      episodes: sourceEpisodes.length ? sourceEpisodes : undefined,
    };
    const values = hasSourceData ? getTvShowsSummary(perSourceSegment) : '';
    return (
      '<span class="tv-source-row">' +
      `<span class="tv-source-name">${sourceLabel}</span>` +
      `<span class="tv-source-values">${values}</span>` +
      '</span>'
    );
  }).join('<span class="tv-source-divider"></span>');
}

// PHOB-1857: which unified TV sources actually have selected shows. Used by the activation modal to
// grey out (and default away from) a source with no shows. Keys off tv/episodes `source` tags only.
function getUnifiedTvSourcesWithShows(audienceSegment) {
  const sourcesInShows = new Set();
  (audienceSegment || [])
    .filter((row) => TV_SHOWS_SEGMENT_TYPES.includes(row.type))
    .forEach((row) => {
      [...(row.tv || []), ...(row.episodes || [])].forEach((item) => {
        if (item.source) sourcesInShows.add(item.source);
      });
    });
  return UNIFIED_TV_SOURCES.map((source) => source.value).filter((value) => sourcesInShows.has(value));
}

// PHOB-1857: a unified TV segment carries "shared" (non-TV) targeting that applies to BOTH sources
// (interests/websites/lifestyles/apps/1st-party, non-geo demographics, and the shared TV networks/genres)
// plus per-source TV shows. A source is activatable when it has its own shows OR the segment has any such
// shared, non-geo attribute. The mandatory Country geo alone is NOT an attribute — a US-only segment has
// no activatable source (→ Activate disabled). Used by the activation modal (which source to enable) and
// the Activate-button enablement rollup.
const NON_GEO_DEMOGRAPHICS_FIELDS = ['age', 'income', 'ethnicity', 'gender', 'children'];
const SHARED_TARGETING_SEGMENT_TYPES = ['interests', 'websites', 'lifestyle', 'apps', SEGMENT_TYPES.FIRST_PARTY];
function hasSharedNonGeoTargeting(audienceSegment) {
  return (audienceSegment || []).some((row) => {
    if (SHARED_TARGETING_SEGMENT_TYPES.includes(row.type)) return true;
    if (TV_SHOWS_SEGMENT_TYPES.includes(row.type)) return !_.isEmpty(row.networks) || !_.isEmpty(row.genres);
    if (DEMOGRAPHICS_SEGMENT_TYPES.includes(row.type))
      return NON_GEO_DEMOGRAPHICS_FIELDS.some((field) => !_.isEmpty(row[field]));
    return false;
  });
}
function getUnifiedTvActivatableSources(audienceSegment) {
  if (hasSharedNonGeoTargeting(audienceSegment)) return UNIFIED_TV_SOURCES.map((source) => source.value);
  return getUnifiedTvSourcesWithShows(audienceSegment);
}

function getSegmentValuesSummary(segment, filtersPartition, isAudienceInfoTooltip = false) {
  const { type, label } = segment;
  const isUnifiedTvSegment = !isAudienceInfoTooltip && Array.isArray(segment.sources);
  const getTvSummary = () =>
    isUnifiedTvSegment ? getUnifiedTvShowsSummary(segment) : getTvShowsSummary(segment, isAudienceInfoTooltip);
  const SUMMARY_FUNC_BY_TYPE = {
    lifestyle: () => label,
    custom_segment: () => label,
    '1st party': () => segment.label,
    demographics: () => getDemographicsValueSummary(segment, filtersPartition),
    country: () => getDemographicsValueSummary(segment, filtersPartition),
    interests: () => getInterestsAndWebsitesValueSummary(segment),
    websites: () => getInterestsAndWebsitesValueSummary(segment),
    linkedinDemographics: () => getLinkedinDemographicsValueSummary(segment),
    linkedinIndustries: () => getLinkedinIndustriesValueSummary(segment),
    linkedinCompanies: () => getLinkedinCompaniesValueSummary(segment),
    linkedinJobs: () => getLinkedinJobsValueSummary(segment),
    tvShows: getTvSummary,
    advancedTvShows: getTvSummary,
    smartTvDemographics: () => getDemographicsValueSummary(segment, filtersPartition),
    apps: () => getInterestsAndWebsitesValueSummary(segment),
    smartTvCommercials: () => getCommercialsValueSummary(segment, isAudienceInfoTooltip),
  };
  return SUMMARY_FUNC_BY_TYPE[type]?.();
}

function hasActivateAudiencePermission(abiPermissions, channel) {
  switch (channel) {
    case CHANNELS.articles?.name:
    case CHANNELS.bidstream?.name:
      return abiPermissions.hasPermission(PERMISSION.WEB);
    case CHANNELS.smart_tv_inscape?.name:
    case CHANNELS.tivo?.name:
    case CHANNELS.hisense?.name:
    case CHANNELS.peerlogix?.name:
    case CHANNELS.unified_tv?.name:
      // unified_tv is permitted so the controller loads marketsAndAdvertisersPromise for it (the
      // activation modal reads it). This does NOT trigger a generic 'unified_tv' size query:
      // updateAudienceAppForChannel computes unified enablement by rolling up the per-source
      // (hisense/peerlogix) isActivateAudienceEnabled results and never calls it with 'unified_tv'.
      return abiPermissions.hasPermission(PERMISSION.TV_ACTIVATION);
    default:
      return false;
  }
}

/**
 * @param abiPermissions - permissions manager
 * @param channel - valid channels can be 'articles'/'smart_tv_inscape'/'tivo'.
 * @param audience
 * @param userId
 * @returns {Promise<{isEnabled: boolean, isVisible: boolean, disabledText: string}|{isEnabled: boolean, isVisible: boolean}>}
 * reasons:
 *   channel - the channel can not be activated
 *   no geo - the segment must contain a geo section
 *   unsupported geo - the geo section must contain supported country codes (currently only US)
 *   active - audience is already active (or fully-active, in case of smart_tv)
 *   demographics - the resulting demographics will be too small for the audience to be activated
 */
async function isActivateAudienceEnabled(abiPermissions, channel, audience, userId) {
  const hasPermission = hasActivateAudiencePermission(abiPermissions, channel);
  if (!hasPermission) return { isEnabled: false, isVisible: false };

  const isSupportedChannel =
    channel === CHANNELS.articles?.name || channel === CHANNELS.bidstream?.name || isSmartTvChannel(channel);
  if (!isSupportedChannel) return { isEnabled: false, isVisible: false };

  const hasAlwaysOnPermission = abiPermissions.hasPermission(PERMISSION.AUDIENCE_ACTIVATION_ALWAYS_ON);
  const isArticlesOrBidstream = channel === CHANNELS.articles?.name || channel === CHANNELS.bidstream?.name;
  const canActivateCustom =
    !audience.activated_at &&
    (!audience.activation?.amplified || (!isArticlesOrBidstream && !audience.activation?.deterministic));
  const canActivateAlwaysOn = !audience.activated_at && hasAlwaysOnPermission && !audience.activation?.always_on;
  if (!canActivateCustom && !canActivateAlwaysOn)
    return {
      isEnabled: false,
      isVisible: true,
      disabledText: 'This segment has been activated. To activate again please clone and activate this audience.',
    };

  // smart_tv audiences are always set to US, no need to check demographics
  if (channel === CHANNELS.articles?.name || channel === CHANNELS.bidstream?.name) {
    const geo = getGeoFromAudienceSegment(audience.segment);
    if (!geo)
      return {
        isEnabled: false,
        isVisible: true,
        disabledText: 'Please select a Geo to enable Audience Targeting.',
      };

    const amplificationGeos = [COUNTRIES.US?.cc, COUNTRIES.AU?.cc, COUNTRIES.GB?.cc];
    const isSupportedGeo = amplificationGeos.includes(geo);
    if (!isSupportedGeo)
      return {
        isEnabled: false,
        isVisible: true,
        disabledText: 'We currently do not support Audience Targeting in the selected Geos.',
      };
  } else if (
    [CHANNELS.tivo?.name, CHANNELS.smart_tv_inscape?.name].includes(channel) &&
    Boolean(_.find(audience.segment, { type: '1st party' }))
  ) {
    return {
      isEnabled: false,
      isVisible: true,
      disabledText:
        'We currently do not support Audience Targeting for TV audiences with first party segment criteria.',
    };
  }

  const isBidstream = channel === CHANNELS.articles?.name || channel === CHANNELS.bidstream?.name;
  const minimalSize = 150;
  const isDemographicsTooNarrow = await checkDemographicsTooNarrow(
    audience.segment,
    channel,
    userId,
    isBidstream,
    minimalSize
  );
  if (isDemographicsTooNarrow)
    return {
      isEnabled: false,
      isVisible: true,
      disabledText:
        'The audience you have selected is too narrow for Audience Targeting. Please expand your audience criteria.',
    };

  return { isEnabled: true, isVisible: true };
}

async function checkDemographicsTooNarrow(segment, channel, userId, isBidstream, minimalSize) {
  const size = await getAudienceSize(segment, {
    channel,
    userId,
    isBidstream,
    minimalSize,
    filterBidstreamDomains: isBidstream,
  });
  return isAudienceSizeTooSmall(size);
}

function isSmartTvChannel(channel) {
  return [
    CHANNELS.smart_tv_inscape?.name,
    CHANNELS.tivo?.name,
    CHANNELS.hisense?.name,
    CHANNELS.peerlogix?.name,
    CHANNELS.unified_tv?.name,
  ].includes(channel);
}

function isAdvancedTvChannel(channel) {
  return [
    CHANNELS.tivo?.name,
    CHANNELS.hisense?.name,
    CHANNELS.peerlogix?.name,
    CHANNELS.unified_tv?.name,
  ].includes(channel);
}

function getGeoFromAudienceSegment(segment) {
  const geo = segment.find(({ type }) => /demographics|country/i.test(type))?.geo || {};
  return _.isArray(geo) ? geo[0].cc : geo?.cc;
}

// Returns the geo *object* ({ label, cc, ... }) from a segment's Country row, or undefined
// when there is none. Unlike getGeoFromAudienceSegment (which returns the cc string and also
// falls back to demographics rows), this is scoped to the mandatory-geo Country row so callers
// can re-sync context.current.audience_app.default_geo — which is the object, not the cc — when
// an audience is loaded. Without this sync the country-gated view (disabled segment types,
// hidden Explore Audience) keeps the previously selected geo's restrictions. PHOB-1813 follow-up.
function getCountryGeoFromSegment(segment) {
  return _.find(segment || [], { type: 'country' })?.geo?.[0];
}

function isDeterministicDynamicActivationAllowed(segment) {
  return !_.some(
    segment || [],
    (s) => (TV_SHOWS_SEGMENT_TYPES + COMMERCIALS_SEGMENT_TYPES).includes(s.type) && !s.timeframe
  );
}

function isCategoryTargetingVisible(abiPermissions) {
  return abiPermissions.hasPermission(PERMISSION.AUDIENCE_CATEGORY_TARGETING);
}

/** Channel/geo rules shared by Audience Affinities and Purchase Affinities explorer tabs. */
function isCategoriesExplorerEligible(segment, channel) {
  const safeSegment = Array.isArray(segment) ? segment : [];
  const hasFirstParty = safeSegment.some((s) => s.type === SEGMENT_TYPES.FIRST_PARTY);
  const webWithFirstParty = channel === CHANNEL_IDS.ARTICLES && hasFirstParty;

  const demographicsDef = safeSegment.find((s) => DEMOGRAPHICS_SEGMENT_TYPES.includes(s.type));
  const isUS = _.get(demographicsDef, 'geo[0].cc') === 'US' || channel === CHANNEL_IDS.PEERLOGIX;

  const categoriesSupportedChannels = [CHANNEL_IDS.BIDSTREAM, CHANNEL_IDS.HISENSE, CHANNEL_IDS.PEERLOGIX];
  return (categoriesSupportedChannels.includes(channel) || webWithFirstParty) && isUS;
}

function isCategoriesEnabled(segment, channel, abiPermissions) {
  if (!abiPermissions.hasPermission(PERMISSION.AUDIENCE_CATEGORIES)) return false;
  return isCategoriesExplorerEligible(segment, channel);
}

function isPurchaseAffinitiesEnabled(segment, channel, abiPermissions) {
  if (!abiPermissions.hasPermission(PERMISSION.PURCHASE_AFFINITIES)) return false;
  return isCategoriesExplorerEligible(segment, channel);
}

Object.assign(exports, {
  convertAudienceSegmentToLogicStatement,
  convertAudienceSegmentToFilterMapping,
  geoByChannel,
  getSegmentValuesSummary,
  hasActivateAudiencePermission,
  isActivateAudienceEnabled,
  isSmartTvChannel,
  isAdvancedTvChannel,
  isAllLevelsOfExposure,
  convertTvShowsToNewLogicStatement,
  SEGMENT_ROW_TYPES,
  CHANNELS,
  COUNTRIES,
  isFranceGeo,
  SEGMENT_ROW_MAP,
  LOGICAL_OPERANDS,
  AND_LOGICAL_OPERAND,
  TV_SHOWS_SEGMENT_TYPES,
  COMMERCIALS_SEGMENT_TYPES,
  DEMOGRAPHICS_SEGMENT_TYPES,
  SEGMENT_TYPES,
  GENDER_VALUES,
  AUDIENCE_AGE_VALUES,
  ETHNICITY_VALUES,
  SG_ETHNICITY_VALUES,
  INCOME_VALUES,
  DISPLAY_INCOME_VALUES,
  LEVELS_OF_EXPOSURE,
  LEVELS_OF_EXPOSURE_MAP,
  LEVEL_OF_EXPOSURE_COMMERCIALS_TOOLTIP,
  LEVEL_OF_EXPOSURE_TV_SHOWS_TOOLTIP,
  VIEWERSHIP_MODES,
  VIEWING_TYPES_MAP,
  VIEWING_TYPES,
  UNIFIED_TV_SOURCES,
  getUnifiedTvSourcesWithShows,
  getUnifiedTvActivatableSources,
  SYNDICATION_VALUES,
  VIEWING_EVENT_QUALIFIERS,
  getGeoFromAudienceSegment,
  getCountryGeoFromSegment,
  isDeterministicDynamicActivationAllowed,
  parseToNumber,
  mergeRangesForSummary,
  dateToString,
  getSummaryTextBuilder,
  deterministicPermissionName,
  customPricePermissionName,
  optimizeSegmentPermissionName,
  DEFAULT_MANDATORY_GEO,
  showAudienceIpCountPermissionName,
  isCategoriesEnabled,
  isPurchaseAffinitiesEnabled,
  isCategoryTargetingVisible,
});
